import { type createContactFormSchema } from "@/components/features/contact/AddOrEditContactModal/AddContactSlideover";
import {
  Button,
  Form,
  FormSubmitButton,
  PhoneNumberFormField,
  SheetFooter,
  SimpleSelectFormField,
  Slideover,
  TextFormField,
  Typography,
} from "@/components/ui";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { CheckboxFormField } from "@/components/ui/FormFields/CheckboxFormField/CheckboxFormField";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import {
  useCreateContactPhone,
  useUpdateContactPhone,
} from "@/lib/data/react-query";
import { ContactPhoneSchema } from "@/lib/data/schemas";
import { getContactPhoneLabelOptions } from "@/lib/data/static/contactStatics";
import { type ContactType, type ContactPhone } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { ContactPhoneIcon } from "@/lib/utils/iconUtils";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm, useWatch, type UseFormReturn } from "react-hook-form";

import type { z } from "zod";

interface AddOrEditContactPhoneNumberSlideoverProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  editing: boolean;
  data?: ContactPhone;
  contactId: string;
  onDelete?: () => void;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
  parentForm?: UseFormReturn<z.infer<typeof createContactFormSchema>>;
  organizationSubTypes?: readonly string[] | null;
  type?: ContactType | null;
  variant?: "slideover" | "inline";
}

type ConditionalProps =
  | {
      editing: true;
      activeContactPhoneNumber: Pick<ContactPhone, "id" | "dateModified">;
    }
  | {
      editing: false;
      activeContactPhoneNumber?: never;
    };

const contactPhoneNumberFormSchema = ContactPhoneSchema.omit({
  firmId: true,
  dateCreated: true,
  contactId: true,
  id: true,
});

const defaultObject: z.input<typeof contactPhoneNumberFormSchema> = {
  number: "",
  description: "",
  label: "",
  extension: "",
  isPrimary: false,
  dateModified: "",
};

export const AddOrEditContactPhoneNumberSlideover = ({
  activeContactPhoneNumber,
  contactId,
  editing,
  hasDeletePermission,
  hasEditPermission,
  onDelete,
  open,
  parentForm,
  setOpen,
  organizationSubTypes,
  type,
  variant = "slideover",
}: AddOrEditContactPhoneNumberSlideoverProps & ConditionalProps) => {
  const form = useForm<z.infer<typeof contactPhoneNumberFormSchema>>({
    resolver: zodResolver(contactPhoneNumberFormSchema),
    values: editing
      ? { ...defaultObject, ...activeContactPhoneNumber }
      : defaultObject,
    mode: "onSubmit",
  });
  const liveFieldValues = useWatch({ control: form.control });
  const createContactPhoneNumber = useCreateContactPhone();
  const updateContactPhoneNumber = useUpdateContactPhone();
  const isFormLoading =
    createContactPhoneNumber.isPending || updateContactPhoneNumber.isPending;

  const onSubmitParentForm = (
    data: z.infer<typeof contactPhoneNumberFormSchema>,
  ) => {
    if (!parentForm) return;
    if (editing) {
      const existingPhones = parentForm.getValues("phones") ?? [];
      parentForm.setValue(
        "phones",
        existingPhones.map((phone) =>
          phone.id === activeContactPhoneNumber.id
            ? {
                ...data,
                id: activeContactPhoneNumber.id,
              }
            : phone,
        ),
        { shouldDirty: true },
      );
      return data;
    } else {
      const existingPhones = parentForm.getValues("phones") ?? [];
      parentForm.setValue(
        "phones",
        [...existingPhones, { ...data, id: Math.random().toString() }],
        { shouldDirty: true },
      );
      return data;
    }
  };

  const onSubmit = async (
    data: z.infer<typeof contactPhoneNumberFormSchema>,
  ) => {
    if (parentForm) {
      onSubmitParentForm(data);
      setOpen(false);
      return;
    }
    if (editing) {
      const dirtyKeys = Object.keys(form.formState.dirtyFields) as Array<
        keyof z.infer<typeof contactPhoneNumberFormSchema>
      >;
      const dirtyData: Partial<z.infer<typeof contactPhoneNumberFormSchema>> =
        {};
      for (const key of dirtyKeys) {
        (dirtyData as Record<string, unknown>)[key] = data[key];
      }
      await updateContactPhoneNumber.mutateAsync({
        contactId,
        id: activeContactPhoneNumber.id,
        ...dirtyData,
        dateModified: activeContactPhoneNumber.dateModified,
      });
    } else {
      await createContactPhoneNumber
        .mutateAsync({
          contactId,
          ...data,
          dateModified: undefined,
        })
        .then(() => {
          form.reset();
        });
    }
    setOpen(false);
  };

  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  /** Keep parent contact form (and live preview) in sync while editing inline — nested fields only flush on submit otherwise. */
  useEffect(() => {
    if (!parentForm || !open || !editing) {
      return;
    }
    const id = activeContactPhoneNumber.id;
    const existingPhones = parentForm.getValues("phones") ?? [];
    parentForm.setValue(
      "phones",
      existingPhones.map((phone) =>
        phone.id === id ? { ...phone, ...liveFieldValues, id } : phone,
      ),
      {
        shouldDirty: form.formState.isDirty,
        shouldValidate: false,
        shouldTouch: false,
      },
    );
  }, [
    activeContactPhoneNumber,
    editing,
    form.formState.isDirty,
    liveFieldValues,
    open,
    parentForm,
  ]);

  const title = editing ? "Update phone" : "Create phone";
  const isInline = variant === "inline";
  const fieldsetClassName = cn(
    slideoverFieldsetClassName,
    isInline && "p-0 my-2 gap-3",
  );
  const handleInlineSubmit = () => {
    void form.handleSubmit(onSubmit)();
  };
  const formContent = (
    <Form {...form}>
      <form
        className={cn(
          "relative flex grow flex-col",
          isInline ? "overflow-visible" : "overflow-hidden",
        )}
        onSubmit={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          await form.handleSubmit(onSubmit)();
        }}
      >
        {isFormLoading && <FormLoadingOverlay />}
        <div
          className={cn(
            "overflow-y-auto",
            variant === "inline" && "overflow-visible",
          )}
        >
          <fieldset
            disabled={editing ? !hasEditPermission : false}
            className={fieldsetClassName}
          >
            <SimpleSelectFormField
              placeholder="Select a label"
              label="Label"
              register={form.register("label")}
              items={getContactPhoneLabelOptions(type, organizationSubTypes)}
            />
            <CheckboxFormField
              register={form.register("isPrimary")}
              label="Primary phone"
              className={variant === "inline" ? "mt-0 h-auto" : undefined}
            />
            <PhoneNumberFormField
              register={form.register("number")}
              label="Phone number*"
            />
            <TextFormField
              register={form.register("extension")}
              label="Extension"
            />
          </fieldset>
        </div>
        <div className="flex grow" />
        <SheetFooter addSlideoverStyles={variant === "slideover"}>
          {hasDeletePermission && editing && (
            <Button type="button" variant="destructive" onClick={onDelete}>
              Delete
            </Button>
          )}
          <div className="flex grow justify-end gap-x-2">
            <FormCancelButton
              setOpen={setOpen}
              isDirty={form.formState.isDirty}
            />
            <FormSubmitButton
              type={isInline ? "button" : "submit"}
              submitFn={isInline ? handleInlineSubmit : undefined}
            />
          </div>
        </SheetFooter>
      </form>
    </Form>
  );

  if (variant === "inline") {
    if (!open) return null;
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-2">
          <ContactPhoneIcon className="h-5 w-5 text-gray-400" />
          <div className="space-y-1">
            <Typography weight="medium">{title}</Typography>
          </div>
        </div>
        {formContent}
      </div>
    );
  }

  return (
    <Slideover
      open={open}
      setOpen={setOpen}
      title={title}
      titleIcon={<ContactPhoneIcon className="h-4 w-4" />}
      isLoading={isFormLoading}
      isDirty={form.formState.isDirty}
    >
      {formContent}
    </Slideover>
  );
};
